import { AlertErrorCircle, CheckCircle, FilePdf } from '@/assets/icons'
import { Link } from '@/components/link/Link'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'
import { matterPaths } from '@/routing/paths'
import { MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import { MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import { TimelineAssetTabs } from '@/timeline/asset/constants'
import { DeletePreviewUsageType, type DeletePreviewInUseFile, type DeletePreviewResponse } from '@/user_files/delete/schemas'
import { getEmailDeleteScopeCopy } from '@/user_files/utils/email-attachment-scope-copy'

interface DeleteItemsDialogProps {
  open: boolean
  /**
   * Files and folders the user ticked, counted the way the user ticked them: a folder is one item
   * rather than the files inside it, and a file is still counted when the folder containing it is
   * ticked too. This is deliberately not the set the request carries — `useDeleteDialog` prunes a
   * covered item out of that, because a folder delete already cascades to it.
   *
   * Counting the selection rather than the deleted files is the FE-594 design call on bug-bash
   * finding BB-04 (2026-08-28): the headline and the contextual toolbar name the same things, so
   * the two can never show different numbers for one selection.
   */
  fileCount: number
  folderCount: number
  /**
   * Extracted attachments that will also be deleted with selected emails (same-folder grouping).
   * Used only for confirmation copy; the request still sends parent file ids.
   */
  extractedAttachmentCount?: number
  /** Server-resolved impact of the selection, including files inside selected folders. */
  preview: DeletePreviewResponse | null
  previewLoading: boolean
  onClose: () => void
  onConfirm: () => void
  onConfirmNotInUse: () => void
  isDeleting: boolean
  error: string | null
}

const kInUseWarningOne = 'This file is being referenced by the matter record. Deleting it removes the source file and breaks those references.'
const kInUseWarningMany = 'Some files are being referenced by the matter record. Deleting them removes the source file and breaks those references.'

/** "Delete all N" only reads naturally from three up; one and two get their own copy. */
function deleteEverythingLabel(fileCount: number): string {
  if (fileCount === 1) return 'Delete file'
  if (fileCount === 2) return 'Delete both'
  return `Delete all ${fileCount}`
}

/**
 * Record types this build can name and deep-link. A file whose usage type is missing here is still
 * listed as in use — losing the link is acceptable, silently dropping the warning is not.
 * `matter_id` comes from the API per usage, because a file can be in use by another matter's record.
 */
const kUsageLinks: Record<string, { label: string; path: (matterId: string) => string }> = {
  [DeletePreviewUsageType.BillingSummary]: {
    label: 'Medical Billing Summary',
    path: (matterId) => matterPaths.billing.assetTab(matterId, MedSummaryAssetTabs.SOURCE_FILES),
  },
  [DeletePreviewUsageType.MedChron]: {
    label: 'Medical Chronology',
    path: (matterId) => matterPaths.chronology.tab(matterId, MedChronAssetTabs.SOURCE_FILES),
  },
  [DeletePreviewUsageType.Timeline]: {
    label: 'Timeline',
    path: (matterId) => matterPaths.timeline.tab(matterId, TimelineAssetTabs.SOURCE_FILES),
  },
}

/** One in-use file: file-type symbol, name, and a link per matter record that uses it. */
function InUseFileRow({ file }: { file: DeletePreviewInUseFile }) {
  return (
    <div role="listitem" className="flex items-center gap-4 px-4 py-2">
      <div className="flex items-center justify-center shrink-0 p-1.5 rounded bg-brand-neutral-100">
        {/* Always the PDF glyph: these records only ingest PDFs today, and the preview response
            carries no content type to pick from. */}
        <FilePdf className="h-5 w-5" />
      </div>
      <div className="flex flex-col justify-center gap-1 min-w-0 flex-1">
        <p className="text-sm font-medium tracking-[-0.15px] text-brand-neutral-950 truncate" title={[...file.display_path, file.name].join('/')}>
          {file.name}
        </p>
        <div className="flex flex-wrap items-center gap-x-3">
          {file.usages.map((usage) => {
            const link = kUsageLinks[usage.type]
            if (!link) return null
            return (
              <Link key={`${usage.type}-${usage.matter_id}`} href={link.path(usage.matter_id)} size="xs" external>
                {link.label}
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * DeleteItemsDialog
 *
 * Unified confirmation dialog for deleting files, folders, or both. When the delete preview
 * reports files in use by a Medical Chronology, Timeline, or Medical Billing Summary, renders the
 * severe warning (PROD-628, Figma 6633:36347) instead of the plain description, and offers a
 * secondary action that deletes only the files that are not in use. Receives all state from the
 * parent hook.
 */
export function DeleteItemsDialog({
  open,
  fileCount,
  folderCount,
  extractedAttachmentCount = 0,
  preview,
  previewLoading,
  onClose,
  onConfirm,
  onConfirmNotInUse,
  isDeleting,
  error,
}: DeleteItemsDialogProps) {
  const selectedCount = fileCount + folderCount

  if (!open || selectedCount === 0) return null

  const inUseFiles = preview?.in_use_files ?? []
  const hasInUseFiles = inUseFiles.length > 0
  const notInUseCount = preview?.delete_not_in_use_file_count ?? 0
  const allCount = preview?.delete_all_file_count ?? 0
  // Prefer the preview total when folders are not involved: it includes extracted descendants the
  // local listing may not show (search, collapsed tree). Fall back to the local grouping count.
  const previewAttachmentCount = !previewLoading && preview && folderCount === 0 && fileCount > 0 ? Math.max(0, preview.delete_all_file_count - fileCount) : 0
  const resolvedAttachmentCount = Math.max(extractedAttachmentCount, previewAttachmentCount)
  const emailScopeCopy = getEmailDeleteScopeCopy({ fileCount, folderCount, extractedAttachmentCount: resolvedAttachmentCount })

  // One title for both cases: the selection, never the preview's file total. The in-use variant used
  // to retitle itself from `delete_all_file_count` — every file the selection covers — so ticking a
  // folder of seven read "Delete 7 files?" beside a toolbar reading "1 item selected". The file
  // total is still on screen, on the primary button, where it is labelled as files.
  const noun = folderCount > 0 && fileCount > 0 ? 'item' : folderCount > 0 ? 'folder' : 'file'
  const title = emailScopeCopy?.title ?? `Delete ${selectedCount} ${selectedCount === 1 ? noun : `${noun}s`}?`

  const description =
    emailScopeCopy?.description ??
    (folderCount > 0 && fileCount > 0
      ? `${folderCount === 1 ? '1 folder' : `${folderCount} folders`} and ${fileCount === 1 ? '1 file' : `${fileCount} files`} will be deleted permanently.`
      : folderCount > 0
      ? folderCount === 1
        ? '1 folder will be deleted permanently.'
        : `${folderCount} folders will be deleted permanently.`
      : fileCount === 1
      ? '1 file will be deleted permanently.'
      : `${fileCount} files will be deleted permanently.`)

  // The warning describes the referenced files, so it pluralizes on those rather than on the whole
  // selection: one in-use file among five still reads "This file is being referenced".
  const inUseWarning = inUseFiles.length === 1 ? kInUseWarningOne : kInUseWarningMany

  // While the check is in flight the dialog does not yet know which of the two descriptions applies,
  // so it shows neither. Stating the plain one first and swapping it for the warning a moment later
  // reads as the dialog changing its mind about what you are about to do.
  const subtitle = previewLoading ? undefined : hasInUseFiles ? inUseWarning : description

  return (
    <Modal open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <ModalContent
        title={title}
        subtitle={subtitle}
        width="md"
        height="max-md"
        className="max-w-[568px]"
        // Full-bleed so the in-use list dividers span the modal edge to edge (Figma). Cancels
        // ModalContent's own `p-6` body padding, so it has to track that value; the rows and the
        // error below re-add their own horizontal padding.
        bodyClassName="-mx-6"
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            tertiaryAction={{ label: 'Cancel', onClick: onClose, variant: 'outlined' }}
            secondaryAction={
              hasInUseFiles && notInUseCount > 0
                ? {
                    label: notInUseCount === 1 ? 'Delete 1 file' : `Delete ${notInUseCount} files`,
                    onClick: onConfirmNotInUse,
                    disabled: isDeleting,
                    color: 'destructive',
                  }
                : undefined
            }
            primaryAction={{
              label: hasInUseFiles ? deleteEverythingLabel(allCount) : 'Delete',
              onClick: onConfirm,
              disabled: previewLoading,
              loading: isDeleting ? 'loading' : undefined,
            }}
          />
        }
      >
        {previewLoading && (
          <div className="flex items-center justify-center gap-3 py-4">
            <CircularProgressContinuousSized size={18} thickness={6} />
            <p className="text-sm text-brand-neutral-700">Checking for references…</p>
          </div>
        )}
        {hasInUseFiles && (
          <div className="flex flex-col border-y border-brand-neutral-200 divide-y divide-brand-neutral-200">
            {/* The affected files are the warning, so expose them as a list a screen reader can
                enumerate. The summary row below is not one of them and stays outside. */}
            <div role="list" aria-label="Files in use by a matter record" className="flex flex-col divide-y divide-brand-neutral-200">
              {inUseFiles.map((file) => (
                <InUseFileRow key={file.id} file={file} />
              ))}
            </div>
            {notInUseCount > 0 && (
              <div className="flex items-center gap-2 min-h-[48px] pl-4 pr-3 py-1.5">
                <CheckCircle className="w-4 h-4 shrink-0 text-brand-neutral-800" />
                <p className="text-sm tracking-[-0.15px] text-brand-neutral-800">
                  {notInUseCount === 1 ? '1 other selected file has' : `${notInUseCount} other selected files have`} no references to matter records.
                </p>
              </div>
            )}
          </div>
        )}
        {error && (
          <p className="mx-6 mt-4 p-2 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
            <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
            {error}
          </p>
        )}
      </ModalContent>
    </Modal>
  )
}
